import { IconUpload, IconUser } from '@tabler/icons-react'
import { useLocation } from 'react-router'
import Header from '../common/components/header/Header'
import clsx from 'clsx'
import Meta from '../common/Meta'
import scroll from '../common/components/scroll/Scroll.module.css'
import classes from './Settings.module.css'
import {
  TextInput,
  PasswordInput,
  Button,
  Alert,
  Avatar,
  FileButton,
  Group,
  Stack,
  Switch,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { useEffect, useState, useMemo, useRef } from 'react'
import {
  useSettings,
  useUpdateSettings,
  useChangePassword,
  SettingsUpdate,
  ChangePasswordForm,
  SettingsUpdateSchema,
  ChangePasswordSchema,
  TwoFactorVerification,
  useEnableOtpGenerate,
  TwoFactorVerificationSchema,
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  NotificationPreferences,
  NotificationPreferencesSchema,
} from './api'
import { useUploadAsset } from '../assets/api'
import { Typography } from '../common/typography'
import { useDisclosure } from '@mantine/hooks'
import DisableTwoFactorAuthentication from './DisableTwoFactorAuthentication'
import EnableTwoFactorAuthentication from './EnableTwoFactorAuthentication'
import { useProfileFields } from '../onboarding/api'
import ProfileFieldInput from '../onboarding/ProfileFieldInput'

export default function Settings() {
  const location = useLocation()

  const { data: settings } = useSettings()
  const { data: profileFields } = useProfileFields()
  const { data: notificationPreferences } = useNotificationPreferences()
  const { mutate: updateSettings, isPending: isUpdatingSettings } =
    useUpdateSettings()
  const { mutate: changePassword, isPending: isChangingPassword } =
    useChangePassword()
  const { mutate: uploadAsset, isPending: isUploadingAsset } = useUploadAsset()
  const {
    mutate: updateNotificationPreferences,
    isPending: isUpdatingNotificationPreferences,
  } = useUpdateNotificationPreferences()
  const {
    mutate: enableOtp,
    isPending: isEnableOtpPending,
    isSuccess: isEnableOtpSuccess,
    data: dataEnable,
    reset: resetEnableOtp,
  } = useEnableOtpGenerate()

  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [uploadedPhotoId, setUploadedPhotoId] = useState<number | null>(null)
  const [fieldValues, setFieldValues] = useState<
    Record<string, string | number | boolean>
  >({})
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [notificationsSuccess, setNotificationsSuccess] = useState<
    string | null
  >(null)
  const [notificationsError, setNotificationsError] = useState<string | null>(
    null
  )
  const [
    openedEnableModal,
    { open: openEnableModal, close: closeEnableModal },
  ] = useDisclosure(false)
  const [
    openedDisableModal,
    { open: openDisableModal, close: closeDisableModal },
  ] = useDisclosure(false)

  const notificationFormInitialized = useRef(false)

  const sortedProfileFields = useMemo(
    () => [...profileFields].sort((a, b) => a.order - b.order),
    [profileFields]
  )

  const profileForm = useForm<SettingsUpdate>({
    initialValues: {
      first_name: '',
      last_name: '',
    },
    validate: zodResolver(SettingsUpdateSchema),
  })

  const passwordForm = useForm<ChangePasswordForm>({
    initialValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
    validate: zodResolver(ChangePasswordSchema),
  })

  const notificationsForm = useForm<NotificationPreferences>({
    initialValues: {
      push_enabled: true,
      news: true,
      arena_posts: true,
    },
    validate: zodResolver(NotificationPreferencesSchema),
  })

  const twoFactorVerificationForm = useForm<TwoFactorVerification>({
    initialValues: {
      device_name: settings.otp_device_name || '',
      password: '',
    },
    validate: zodResolver(TwoFactorVerificationSchema),
  })

  useEffect(() => {
    if (settings) {
      profileForm.setValues({
        first_name: settings.first_name,
        last_name: settings.last_name,
      })

      if (settings.photo) {
        const photoUrl = Object.values(settings.photo)[0] as string
        if (photoUrl) {
          setAvatarPreview(photoUrl)
        }
      }
    }
  }, [settings])

  useEffect(() => {
    if (settings.fields) {
      const initialValues: Record<string, string | number | boolean> = {}
      Object.entries(settings.fields).forEach(([key, value]) => {
        if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          initialValues[key] = value
        }
      })
      setFieldValues(initialValues)
    }
  }, [settings.fields])

  useEffect(() => {
    if (notificationPreferences && !notificationFormInitialized.current) {
      const values = {
        push_enabled: notificationPreferences.push_enabled ?? true,
        news: notificationPreferences.news ?? true,
        arena_posts: notificationPreferences.arena_posts ?? true,
      }
      notificationsForm.setInitialValues(values)
      notificationsForm.setValues(values)
      notificationFormInitialized.current = true
    }
  }, [notificationPreferences])

  const handleFieldChange = (key: string, value: string | number | boolean) => {
    setFieldValues((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const hasFieldValuesChanged = useMemo(() => {
    if (!settings.fields) return false

    const initialFields = settings.fields as Record<
      string,
      string | number | boolean
    >

    // Check if any field value has changed
    return Object.keys(fieldValues).some((key) => {
      const currentValue = fieldValues[key]
      const initialValue = initialFields[key]

      // Handle empty strings as no change from undefined/null
      if (
        currentValue === '' &&
        (initialValue === undefined || initialValue === null)
      ) {
        return false
      }

      return currentValue !== initialValue
    })
  }, [fieldValues, settings.fields])

  const handleProfileSubmit = (values: SettingsUpdate) => {
    // Filter out empty string values from fields
    const filteredFields = Object.entries(fieldValues).reduce(
      (acc, [key, value]) => {
        if (value !== '') {
          acc[key] = value
        }
        return acc
      },
      {} as Record<string, string | number | boolean>
    )

    const data = {
      ...values,
      ...(uploadedPhotoId && { photo_id: uploadedPhotoId }),
      fields: filteredFields,
    }

    updateSettings(data, {
      onSuccess: () => {
        setProfileSuccess('Profile updated successfully')
        setProfileError(null)
        setUploadedPhotoId(null)
      },
      onError: (error) => {
        setProfileError(error.message)
        setProfileSuccess(null)
      },
    })
  }

  const handlePasswordSubmit = (values: ChangePasswordForm) => {
    changePassword(
      {
        current_password: values.current_password,
        new_password: values.new_password,
      },
      {
        onSuccess: () => {
          setPasswordSuccess('Password changed successfully')
          setPasswordError(null)
          passwordForm.reset()
        },
        onError: (error) => {
          setPasswordError(error.message)
          setPasswordSuccess(null)
        },
      }
    )
  }

  const handleNotificationsSubmit = (values: NotificationPreferences) => {
    updateNotificationPreferences(values, {
      onSuccess: () => {
        setNotificationsSuccess('Notification preferences updated successfully')
        setNotificationsError(null)
        notificationFormInitialized.current = false
      },
      onError: (error) => {
        setNotificationsError(error.message)
        setNotificationsSuccess(null)
      },
    })
  }

  const handleAvatarUpload = (file: File | null) => {
    if (!file) return

    const reader = new FileReader()
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string)
    }
    reader.readAsDataURL(file)

    uploadAsset(
      { file, model_field: 'account.Profile.photo' },
      {
        onSuccess: (assetId) => {
          const profileData = {
            first_name: profileForm.values.first_name,
            last_name: profileForm.values.last_name,
            photo_id: assetId,
          }

          updateSettings(profileData, {
            onSuccess: () => {
              setProfileSuccess('Profile photo updated successfully')
              setProfileError(null)
              setUploadedPhotoId(null)
            },
            onError: (error) => {
              setProfileError(error.message)
              setProfileSuccess(null)
            },
          })
        },
        onError: () => {
          setProfileError('Failed to upload photo')
          setAvatarPreview(null)
        },
      }
    )
  }

  const handleEnableOtpGenerate = (values: TwoFactorVerification) => {
    enableOtp(values, {
      onSuccess: () => {
        openEnableModal()
      },
    })
  }

  return (
    <div className={classes.container}>
      <Header
        title="Settings"
        back={location.state?.from?.pathname || '/news'}
      />
      <Meta title="Settings" />
      <div className={classes.outerSettingsContainer}>
        <div className={clsx(classes.innerContainer, scroll.scroll)}>
          <div className={classes.content}>
            <Stack gap="sm">
              <Typography fw={700} fs="xl" lh="100">
                Personal Information
              </Typography>
              {profileSuccess && (
                <Alert
                  color="green"
                  mb="md"
                  onClose={() => setProfileSuccess(null)}
                  withCloseButton
                >
                  {profileSuccess}
                </Alert>
              )}
              {profileError && (
                <Alert
                  color="red"
                  mb="md"
                  onClose={() => setProfileError(null)}
                  withCloseButton
                >
                  {profileError}
                </Alert>
              )}

              <form
                className={classes.formContainer}
                onSubmit={profileForm.onSubmit(handleProfileSubmit)}
              >
                <Stack className={classes.personalInformation}>
                  <Stack align="center" gap="sm">
                    <Avatar size={92} src={avatarPreview} alt="Profile photo">
                      <IconUser size={50} />
                    </Avatar>
                    <Stack gap="xxs">
                      <FileButton
                        onChange={handleAvatarUpload}
                        accept="image/png,image/jpeg"
                        disabled={isUploadingAsset}
                      >
                        {(props) => (
                          <Button
                            {...props}
                            className={classes.button}
                            leftSection={<IconUpload size={16} />}
                            loading={isUploadingAsset}
                          >
                            <Typography
                              lh="150"
                              fc={{ light: 'white', dark: 'white' }}
                            >
                              Upload Photo
                            </Typography>
                          </Button>
                        )}
                      </FileButton>
                      <Typography
                        fs="xs"
                        ta="center"
                        lh="150"
                        fc={{ light: 'gray-6', dark: 'gray-5' }}
                      >
                        Accepted formats: PNG, JPEG
                      </Typography>
                    </Stack>
                  </Stack>

                  <Group grow className={classes.inputContainer}>
                    <TextInput
                      label="First Name"
                      placeholder="Enter your first name"
                      {...profileForm.getInputProps('first_name')}
                    />
                    <TextInput
                      label="Last Name"
                      placeholder="Enter your last name"
                      {...profileForm.getInputProps('last_name')}
                    />
                  </Group>

                  <div className={classes.profileFieldsContainer}>
                    {sortedProfileFields.map((profileField) => (
                      <ProfileFieldInput
                        key={profileField.key}
                        profileField={profileField}
                        value={fieldValues[profileField.key]}
                        onChange={handleFieldChange}
                      />
                    ))}
                  </div>
                </Stack>
                <Group justify="flex-end">
                  <Button
                    className={classes.button}
                    type="submit"
                    loading={isUpdatingSettings}
                    disabled={
                      !profileForm.isDirty() &&
                      !uploadedPhotoId &&
                      !hasFieldValuesChanged
                    }
                  >
                    <Typography
                      fs="sm"
                      fw={500}
                      fc={{ light: 'white', dark: 'white' }}
                      lh="100"
                      tt="uppercase"
                    >
                      Save Profile
                    </Typography>
                  </Button>
                </Group>
              </form>
            </Stack>

            <Stack gap="sm">
              <Typography fw={700} fs="xl" lh="100">
                Security
              </Typography>

              {passwordSuccess && (
                <Alert
                  color="green"
                  mb="md"
                  onClose={() => setPasswordSuccess(null)}
                  withCloseButton
                >
                  {passwordSuccess}
                </Alert>
              )}
              {passwordError && (
                <Alert
                  color="red"
                  mb="md"
                  onClose={() => setPasswordError(null)}
                  withCloseButton
                >
                  {passwordError}
                </Alert>
              )}

              <form
                className={classes.formContainer}
                onSubmit={passwordForm.onSubmit(handlePasswordSubmit)}
              >
                <Stack className={classes.formWrapper}>
                  <Stack align="center" gap="sm">
                    <PasswordInput
                      className={classes.inputContainer}
                      label="Current Password"
                      placeholder="Enter your current password"
                      {...passwordForm.getInputProps('current_password')}
                    />

                    <PasswordInput
                      className={classes.inputContainer}
                      label="New Password"
                      placeholder="Enter your new password"
                      {...passwordForm.getInputProps('new_password')}
                    />

                    <PasswordInput
                      className={classes.inputContainer}
                      label="Confirm New Password"
                      placeholder="Confirm your new password"
                      {...passwordForm.getInputProps('confirm_password')}
                    />
                  </Stack>
                </Stack>
                <Group justify="flex-end">
                  <Button
                    className={classes.button}
                    type="submit"
                    loading={isChangingPassword}
                    disabled={!passwordForm.isDirty()}
                  >
                    <Typography
                      fs="sm"
                      fw={500}
                      fc={{ light: 'white', dark: 'white' }}
                      lh="100"
                      tt="uppercase"
                    >
                      Change Password
                    </Typography>
                  </Button>
                </Group>
              </form>
            </Stack>

            <Stack gap="sm">
              <Typography fw={700} fs="xl" lh="100">
                Two Factor Authentication
              </Typography>

              <form
                className={classes.formContainer}
                onSubmit={twoFactorVerificationForm.onSubmit(
                  handleEnableOtpGenerate
                )}
              >
                <Stack className={classes.formWrapper}>
                  <Stack align="center" gap="sm">
                    <Typography
                      fs="sm"
                      lh="140"
                      className={classes.inputContainer}
                    >
                      Two-factor authentication adds an additional layer of
                      security to your account by requiring more than just a
                      password to sign in.
                    </Typography>

                    {settings.otp_enabled &&
                    settings.otp_device_name &&
                    settings.otp_device_name?.length > 0 ? (
                      <TextInput
                        className={classes.inputContainer}
                        classNames={{
                          wrapper: classes.inputContainerWrapper,
                          input: classes.inputContainerInput,
                        }}
                        label="Your Two-factor Device"
                        value={settings.otp_device_name}
                        disabled
                      />
                    ) : (
                      <>
                        <TextInput
                          className={classes.inputContainer}
                          label="Add Your OTP Device"
                          placeholder="Enter Your OTP Device Name"
                          {...twoFactorVerificationForm.getInputProps(
                            'device_name'
                          )}
                        />
                        <PasswordInput
                          className={classes.inputContainer}
                          label="Password"
                          placeholder="Enter your password"
                          {...twoFactorVerificationForm.getInputProps(
                            'password'
                          )}
                        />
                      </>
                    )}
                  </Stack>
                </Stack>
                <Group justify="flex-end">
                  {settings.otp_enabled ? (
                    <Button
                      className={classes.disableButton}
                      onClick={openDisableModal}
                    >
                      <Typography
                        fs="sm"
                        fw={500}
                        fc={{ light: 'white', dark: 'white' }}
                        lh="100"
                        tt="uppercase"
                      >
                        Disable two factor authentication
                      </Typography>
                    </Button>
                  ) : (
                    <Button
                      className={classes.button}
                      disabled={
                        isEnableOtpSuccess ||
                        !!(
                          settings.otp_enabled &&
                          settings.otp_device_name &&
                          settings.otp_device_name.length > 0
                        ) ||
                        twoFactorVerificationForm.values.password.trim()
                          .length === 0
                      }
                      loading={isEnableOtpPending}
                      type="submit"
                    >
                      <Typography
                        fs="sm"
                        fw={500}
                        fc={{ light: 'white', dark: 'white' }}
                        lh="100"
                        tt="uppercase"
                      >
                        Create two factor device
                      </Typography>
                    </Button>
                  )}
                </Group>
              </form>
              {isEnableOtpSuccess && dataEnable && (
                <EnableTwoFactorAuthentication
                  openedEnableModal={openedEnableModal}
                  closeEnableModal={closeEnableModal}
                  otp_device_name={settings.otp_device_name || ''}
                  resetEnableOtp={resetEnableOtp}
                  dataEnable={dataEnable}
                />
              )}

              <DisableTwoFactorAuthentication
                openedDisableModal={openedDisableModal}
                closeDisableModal={closeDisableModal}
              />
            </Stack>

            <Stack gap="sm">
              <Typography fw={700} fs="xl" lh="100">
                Notifications
              </Typography>

              {notificationsSuccess && (
                <Alert
                  color="green"
                  mb="md"
                  onClose={() => setNotificationsSuccess(null)}
                  withCloseButton
                >
                  {notificationsSuccess}
                </Alert>
              )}
              {notificationsError && (
                <Alert
                  color="red"
                  mb="md"
                  onClose={() => setNotificationsError(null)}
                  withCloseButton
                >
                  {notificationsError}
                </Alert>
              )}

              <form
                className={classes.formContainer}
                onSubmit={notificationsForm.onSubmit(handleNotificationsSubmit)}
              >
                <Stack className={classes.formWrapper}>
                  <Stack align="center" gap="sm">
                    <Switch
                      {...notificationsForm.getInputProps('push_enabled', {
                        type: 'checkbox',
                      })}
                      label="Enable Notifications"
                      labelPosition="left"
                      classNames={{
                        root: classes.switchRoot,
                        label: classes.switchLabel,
                        body: classes.switchBody,
                        track: classes.switchTrack,
                        thumb: classes.switchThumb,
                      }}
                    />
                    <Switch
                      {...notificationsForm.getInputProps('news', {
                        type: 'checkbox',
                      })}
                      label="News Articles"
                      labelPosition="left"
                      classNames={{
                        root: classes.switchRoot,
                        label: classes.switchLabel,
                        body: classes.switchBody,
                        track: classes.switchTrack,
                        thumb: classes.switchThumb,
                      }}
                    />
                    <Switch
                      {...notificationsForm.getInputProps('arena_posts', {
                        type: 'checkbox',
                      })}
                      label="Arena Posts & Comments"
                      labelPosition="left"
                      classNames={{
                        root: classes.switchRoot,
                        label: classes.switchLabel,
                        body: classes.switchBody,
                        track: classes.switchTrack,
                        thumb: classes.switchThumb,
                      }}
                    />
                  </Stack>
                </Stack>
                <Group justify="flex-end">
                  <Button
                    className={classes.button}
                    type="submit"
                    disabled={
                      !notificationsForm.isDirty() ||
                      isUpdatingNotificationPreferences
                    }
                    loading={isUpdatingNotificationPreferences}
                  >
                    <Typography
                      fs="sm"
                      fw={500}
                      fc={{ light: 'white', dark: 'white' }}
                      lh="100"
                      tt="uppercase"
                    >
                      Update Notifications
                    </Typography>
                  </Button>
                </Group>
              </form>
            </Stack>
          </div>
        </div>
      </div>
    </div>
  )
}
